import React, { useEffect, useState } from "react";
import {
  Loader2, RefreshCw, Home, Mail, Phone, CheckCircle2, AlertTriangle, Clock, Eye, Calendar,
} from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDate, formatDateTime } from "@/lib/format";

const SYNC_BADGE = {
  ok: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Synced" },
  error: { cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle, label: "Sync error" },
  pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, label: "Pending" },
};

function SyncBadge({ status }) {
  const b = SYNC_BADGE[status] || SYNC_BADGE.pending;
  const Icon = b.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${b.cls}`}>
      <Icon className="w-3 h-3" /> {b.label}
    </span>
  );
}

export default function AdminHosts() {
  const { t, locale } = useI18n();
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/hosts");
      setHosts(unwrap(r)?.hosts || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      const r = await api.post("/admin/hosts/sync-all");
      const d = unwrap(r);
      toast.success(`Synced ${d.listings_synced} listing(s) · ${d.bookings_created} new cleaning(s)`);
      await load();
    } catch { toast.error("Sync failed"); }
    finally { setSyncingAll(false); }
  };

  const syncOne = async (listingId) => {
    setSyncingId(listingId);
    try {
      const r = await api.post(`/admin/hosts/listings/${listingId}/sync`);
      const res = unwrap(r)?.result || {};
      if (res.ok) toast.success(`${res.events_detected} event(s) · ${res.bookings_created} new cleaning(s)`);
      else toast.error(`Sync error: ${res.error || "unknown"}`);
      await load();
    } catch { toast.error("Sync failed"); }
    finally { setSyncingId(null); }
  };

  const openDetail = async (listingId) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const r = await api.get(`/admin/hosts/listings/${listingId}`);
      setDetail(unwrap(r));
    } catch { toast.error("Could not load listing"); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-hosts">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("admin.hosts")}</h1>
          <p className="text-[#4B5563] text-sm mt-1">{hosts.length} connected host(s) · Airbnb auto-cleaning</p>
        </div>
        <Button onClick={syncAll} disabled={syncingAll} variant="outline" className="rounded-xl" data-testid="hosts-sync-all-btn">
          {syncingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Sync all
        </Button>
      </div>

      {hosts.length === 0 ? (
        <Card className="rounded-2xl border border-[#E5E7EB] p-16 text-center text-[#4B5563]">
          <Home className="w-7 h-7 mx-auto mb-2 text-[#9CA3AF]" />
          No hosts connected yet.
        </Card>
      ) : (
        <div className="space-y-4">
          {hosts.map((h) => (
            <Card key={h.user_id} className="rounded-2xl border border-[#E5E7EB] overflow-hidden" data-testid={`host-card-${h.user_id}`}>
              <div className="p-5 bg-[#FAFAF9] border-b border-[#E5E7EB] flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-display text-lg font-bold">{h.name}</div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#4B5563] mt-1">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {h.email}</span>
                    {h.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {h.phone}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-5 text-sm">
                  <Stat label="Listings" value={h.active_listings} />
                  <Stat label="Auto bookings" value={h.total_auto_bookings} />
                  <Stat label="Next cleaning" value={h.next_cleaning ? formatDate(h.next_cleaning, locale) : "—"} />
                  <SyncBadge status={h.last_sync_status} />
                </div>
              </div>
              <div className="divide-y divide-[#E5E7EB]">
                {h.listings.map((l) => (
                  <div key={l.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`host-listing-${l.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold flex items-center gap-2">
                        {l.nickname}
                        {!l.is_active && <Badge variant="outline" className="text-[10px]">inactive</Badge>}
                      </div>
                      <div className="text-xs text-[#9CA3AF] truncate">{l.address?.line1}, {l.address?.city} {l.address?.zip_code}</div>
                      {l.last_sync_status === "error" && l.last_sync_error && (
                        <div className="text-xs text-red-600 mt-1 truncate max-w-md">⚠ {l.last_sync_error}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Next</div>
                        <div className="font-medium flex items-center gap-1">{l.next_cleaning ? <><Calendar className="w-3 h-3" />{formatDate(l.next_cleaning, locale)}</> : "—"}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Bookings</div>
                        <div className="font-medium">{l.auto_bookings_count}</div>
                      </div>
                      <SyncBadge status={l.last_sync_status} />
                      <Button size="sm" variant="outline" className="rounded-lg" onClick={() => syncOne(l.id)} disabled={syncingId === l.id} data-testid={`host-sync-${l.id}`}>
                        {syncingId === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openDetail(l.id)} data-testid={`host-detail-${l.id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="host-detail-modal">
          <DialogHeader><DialogTitle>{detail?.listing?.nickname || "Listing detail"}</DialogTitle></DialogHeader>
          {detailLoading || detail?.loading ? (
            <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : detail?.listing ? (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Host" value={detail.host?.name} />
                <Info label="Email" value={detail.host?.email} />
                <Info label="Address" value={`${detail.listing.address?.line1}, ${detail.listing.address?.city}`} />
                <Info label="Cleaning offset" value={`+${detail.listing.cleaning_offset_days} day(s)`} />
                <Info label="iCal URL" value={detail.listing.ical_url} mono />
              </div>

              <div>
                <h4 className="font-semibold text-sm mb-2">Sync log</h4>
                <div className="rounded-xl border border-[#E5E7EB] overflow-hidden">
                  {(detail.listing.sync_log || []).length === 0 ? (
                    <div className="p-3 text-xs text-[#9CA3AF]">No syncs yet.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead><tr className="bg-[#FAFAF9] text-left text-[#6B7280]">
                        <th className="px-3 py-2">When</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Events</th><th className="px-3 py-2">Created</th><th className="px-3 py-2">Error</th>
                      </tr></thead>
                      <tbody className="divide-y divide-[#E5E7EB]">
                        {detail.listing.sync_log.map((s, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 font-mono">{formatDateTime(s.at, locale)}</td>
                            <td className="px-3 py-2">{s.status === "ok" ? "✅" : "❌"} {s.status}</td>
                            <td className="px-3 py-2">{s.events_detected} / +{s.bookings_created}</td>
                            <td className="px-3 py-2">{s.bookings_created}</td>
                            <td className="px-3 py-2 text-red-600 truncate max-w-[160px]">{s.error || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-sm mb-2">Auto bookings ({detail.bookings?.length || 0})</h4>
                <div className="rounded-xl border border-[#E5E7EB] overflow-hidden">
                  {(detail.bookings || []).length === 0 ? (
                    <div className="p-3 text-xs text-[#9CA3AF]">No bookings created yet.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead><tr className="bg-[#FAFAF9] text-left text-[#6B7280]">
                        <th className="px-3 py-2">Booking #</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Total</th>
                      </tr></thead>
                      <tbody className="divide-y divide-[#E5E7EB]">
                        {detail.bookings.map((b) => (
                          <tr key={b.id}>
                            <td className="px-3 py-2 font-mono">{b.booking_number}</td>
                            <td className="px-3 py-2">{formatDate(b.preferred_date, locale)}</td>
                            <td className="px-3 py-2 capitalize">{b.status}</td>
                            <td className="px-3 py-2 text-right font-mono">${(b.total || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{label}</div>
      <div className={`font-medium break-words ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
    </div>
  );
}
